import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useApi } from "./Api";
import { useWallet } from "./WalletConnect";
import { getGasFee } from "@/services/bridgeService";
import { BN, BN_ZERO } from "@polkadot/util";

interface BalanceState {
  totalBalance: string;
  isBalanceLoading: boolean;
  availableToBridge: string;
  fetchBalance: () => Promise<void>;
}

export const BalanceContext = createContext<BalanceState>(
  {} as unknown as BalanceState
);

export function BalanceProvider({ children }: React.PropsWithChildren) {
  const [totalBalance, setTotalBalance] = useState<string>("0");
  const [availableToBridge, setAvailableToBridge] = useState<string>("0");
  const [isBalanceLoading, setIsBalanceLoading] = useState(true);

  const { api, isReady } = useApi();
  const { selectedAccount } = useWallet();

  const fetchBalance = async () => {
    if (!isReady || !api || !selectedAccount?.address) {
      setTotalBalance("0");
      setAvailableToBridge("0");
      return;
    }
    const balance = await api.derive.balances.all(selectedAccount.address);
    setTotalBalance(balance.freeBalance.toString());
    const gasFee = await getGasFee(api, selectedAccount, {
      amount: balance.availableBalance.toString(),
      chain: "Base",
      receipientAddress: "0x0000000000000000000000000000000000000000", // Placeholder address
    });
    if (gasFee) {
      const availableToStake = balance.availableBalance
        ?.sub(new BN(10 ** 12))
        ?.sub(gasFee);
      setAvailableToBridge(
        availableToStake?.gt(BN_ZERO)
          ? availableToStake?.toString() ?? "0"
          : "0"
      );
    } else {
      setAvailableToBridge("0");
    }
    setIsBalanceLoading(false);
  };

  const value = useMemo(
    () => ({
      totalBalance,
      isBalanceLoading,
      availableToBridge,
      fetchBalance,
    }),
    [totalBalance, isBalanceLoading, availableToBridge]
  );

  useEffect(() => {
    fetchBalance();
  }, [api, isReady, selectedAccount]);

  return (
    <BalanceContext.Provider value={value}>{children}</BalanceContext.Provider>
  );
}

export const useBalance = () => useContext(BalanceContext);
